import SVGIcon from 'components/SVGIcon/SVGIcon';
import contactUsPageMessages from 'containers/ContactUsPage/messages';
import useDMCA from 'hooks/useDMCA';
import React, { FC, ImgHTMLAttributes } from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styles/styled-components';
import { commonDimensions } from 'utils/constants';
import { generateUrlBrandDetail } from 'utils/generateBrandDetailUrl';
import { pathnameChecker } from 'utils/url';
import makeSelectMainLayout from '../../containers/MainLayout/selectors';
import { IMainLayoutStateProps, MainLayoutState, RootState } from '../../containers/MainLayout/types';
import mainNavMessages from '../MainNav/messages';
import KamereoLogoWhite from '../MainNav/NavIcons/KamereoLogoMobileWhite.svg';
import AppStore from './AppStore.svg';
import AppStoreVi from './AppStoreVi.svg';
import EmailIcon from './EmailIcon.svg';
import Facebook from './Facebook.svg';
import GoogleStore from './GoogleStore.svg';
import GoogleStoreVi from './GoogleStoreVi.svg';
import HeadQuarter from './HeadQuarter.svg';
import Instagram from './Instagram.svg';
import Linkedin from './Linkedin.svg';
import PhoneIcon from './PhoneIcon.svg';
import RegisteredIndustryAndTrade from './RegisteredTrade.png?as=webp';
import Tiktok from './Tiktok.svg';
import Youtube from './Youtube.svg';
import Zalo from './Zalo.svg';
import messages from './messages';
import renderWithWebDomain from 'utils/renderWithWebDomain';

interface FooterProps extends InjectedIntlProps, RouteComponentProps {
  mainLayout: MainLayoutState;
}

// ─── SEO (white) Section ─────────────────────────────────────────────────────

const FooterSeoSection = styled.div`
  background: #ffffff;
`;

const FooterSeoInner = styled.nav`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto;
  padding: 20px 40px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  @media (max-width: 991px) {
    padding: 20px 16px 30px;
  }
`;

const FooterCategoryItem = styled.div``;

const FooterCategoryItemName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
  @media (max-width: 991px) {
    font-size: 14px;
  }
`;

const FooterCategorySubItemList = styled.div`
  display: flex;
  align-items: center;
  row-gap: 4px;
  flex-wrap: wrap;
  margin-left: -8px;
`;

const FooterCategorySubItem = styled(Link)`
  padding: 0 8px;
  position: relative;
  font-size: 14px;
  color: #4b5563;
  font-weight: 400;
  line-height: 22px;
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    width: 1px;
    height: 12px;
    background: #d1d5db;
  }
  &:hover {
    color: #43a047;
  }
`;

// ─── Dark Section ─────────────────────────────────────────────────────────────

const DarkSectionWrapper = styled.div`
  background: #111827;
`;

const DarkSectionInner = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto;
  padding: 40px 40px 40px;
  @media (max-width: 991px) {
    padding: 32px 16px 24px;
  }
`;

const DarkMainContent = styled.div`
  display: flex;
  gap: 48px;
  @media (max-width: 1023px) {
    flex-direction: column;
    gap: 32px;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  gap: 16px;
  flex-shrink: 0;
  flex-basis: 582px;
  @media (max-width: 1023px) {
    width: 100%;
    height: fit-content;
    flex-basis: auto;
  }
  @media (max-width: 991px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const KamereoContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  flex: 1 1 55%;
`;

const LogoLink = styled.a`
  display: inline-block;
  & svg {
    width: 160px !important;
    height: 28px !important;
  }
`;

const Tagline = styled.p`
  font-size: 14px;
  color: #ffffff;
  opacity: 0.7;
  margin: 0;
  line-height: 22px;
  font-weight: 400;
`;

const AppBadgesRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const CertBadgesRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 45%;
  font-weight: 400;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const ContactIconWrapper = styled.div`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 4px;
`;

const ContactText = styled.span`
  font-size: 14px;
  color: #ffffff;
  opacity: 0.7;
  line-height: 22px;
`;

const ContactTextAddress = styled.span`
  font-size: 12px;
  color: #ffffff;
  opacity: 0.7;
  line-height: 18px;
  margin-top: -4px;
`;

export const DMCA = styled((props: ImgHTMLAttributes<HTMLImageElement>) => <img {...props} />)`
  max-width: 90px;
`;

const NavColumnsWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  @media (max-width: 991px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px 16px;
  }
`;

const NavColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const NavColumnHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 12px;
`;

const NavColumnLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const navLinkCSS = `
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  opacity: 0.7;
  line-height: 22px;
  &:hover {
    color: #ffffff;
    opacity: 1;
  }
`;

const NavLinkInternal = styled(Link)`
  ${navLinkCSS}
`;

const NavLinkExternal = styled.a`
  ${navLinkCSS}
`;

const DarkDivider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 24px 0 40px;
  @media (max-width: 991px) {
    margin: 24px 0;
  }
`;

const BottomBar = styled.div`
  display: grid;
  grid-template-columns: max-content max-content max-content 1fr;
  gap: 24px;
  font-weight: 400;
  @media (max-width: 991px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: min-content;
  }
`;

const BottomLink = styled.a`
  font-size: 14px;
  color: #ffffff;
  opacity: 0.7;
  &:hover {
    color: #ffffff;
    opacity: 1;
  }
  @media (max-width: 991px) {
    text-align: center;
  }
`;

const CopyrightText = styled.span`
  font-size: 14px;
  color: #ffffff;
  opacity: 0.7;
  @media (max-width: 991px) {
    grid-area: 3 / 1 / 4 / 3;
    text-align: center;
  }
`;

const SocialIconsRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
  @media (max-width: 991px) {
    justify-content: center;
    grid-area: 2 / 1 / 3 / 3;
    justify-content: space-around;
  }
`;

const ContainerBrandIcon = styled.div`
  svg {
    width: 16px;
    height: 16px;
  }
`;

const IconSocial = styled(SVGIcon)<any>`
  & > div > .injected-svg {
    width: ${({ dimension }) => (dimension ? `${dimension}px` : '16px')};
    height: ${({ dimension }) => (dimension ? `${dimension}px` : '16px')};
  }
  &:hover {
    transform: scale(1.1);
    transition: 0.3s ease-in-out;
  }
  @media (max-width: 991px) {
    & > div > .injected-svg {
      width: 32px;
      height: 32px;
    }
  }
`;

// ─── Exports ──────────────────────────────────────────────────────────────────
export const deliveryPolicy =
  window.localStorage.getItem('lang') === 'vi'
    ? 'https://kamereo.vn/blog/wp-content/uploads/2026/06/Chinh-sach-giao-hang-tai-Kamereo-ap-dung-01_07_2026.pdf'
    : 'https://kamereo.vn/blog/wp-content/uploads/2026/06/Delivery-Policy-at-Kamereo-effective-from-July-1st-2026.pdf';

export const SocialNetworkGroup = () => {
  return (
    <SocialIconsRow>
      <a rel="noopener noreferrer" target="_blank" href="https://www.instagram.com/kamereo.vn">
        <ContainerBrandIcon>
          <IconSocial src={Instagram} imgAttr={{ loading: 'lazy', alt: 'Instagram' }} renderAsImg={false} />
        </ContainerBrandIcon>
      </a>
      <a rel="noopener noreferrer" target="_blank" href="https://www.facebook.com/kamereo.vn">
        <ContainerBrandIcon>
          <IconSocial
            src={Facebook}
            imgAttr={{ loading: 'lazy', alt: 'Facebook' }}
            dimension={16}
            renderAsImg={false}
          />
        </ContainerBrandIcon>
      </a>
      <a rel="noopener noreferrer" target="_blank" href="https://zalo.me/2357831519751856428">
        <ContainerBrandIcon>
          <IconSocial src={Zalo} renderAsImg={false} dimension={22} />
        </ContainerBrandIcon>
      </a>
      <a rel="noopener noreferrer" target="_blank" href="https://www.linkedin.com/company/kamereo">
        <ContainerBrandIcon>
          <IconSocial src={Linkedin} imgAttr={{ loading: 'lazy', alt: 'Linkedin' }} renderAsImg={false} />
        </ContainerBrandIcon>
      </a>
      <a rel="noopener noreferrer" target="_blank" href="https://www.youtube.com/channel/UCHtJ6fCFSNZH0DBC8OqhicA">
        <ContainerBrandIcon>
          <IconSocial src={Youtube} imgAttr={{ loading: 'lazy', alt: 'Youtube' }} renderAsImg={false} />
        </ContainerBrandIcon>
      </a>
      <a rel="noopener noreferrer" target="_blank" href="https://www.tiktok.com/@kamereo.vietnam">
        <ContainerBrandIcon>
          <IconSocial src={Tiktok} renderAsImg={false} dimension={12} />
        </ContainerBrandIcon>
      </a>
    </SocialIconsRow>
  );
};

// const customersData = ['restaurant', 'coffee', 'barClub', 'storeSuperMarket', 'foodService', 'eatery'];

// ─── Main Component ───────────────────────────────────────────────────────────

const Footer: FC<FooterProps> = (props) => {
  const {
    mainLayout: { footer },
    location,
  } = props;

  const dmcaRef = useDMCA();
  const isHomePage = pathnameChecker.isHomePage(location.pathname);
  const lang = window.localStorage.getItem('lang') || 'vi';

  return (
    <>
      {/* ── SEO Section ── */}
      <FooterSeoSection>
        <FooterSeoInner>
          {!!footer?.trustedBrands?.length && (
            <FooterCategoryItem>
              <FooterCategoryItemName>
                <FormattedMessage {...messages.trustedBrands} />
              </FooterCategoryItemName>
              <FooterCategorySubItemList>
                {footer.trustedBrands.map((brand) => (
                  <FooterCategorySubItem key={brand?.id} to={generateUrlBrandDetail(brand.id, brand?.name as string)}>
                    {brand.name}
                  </FooterCategorySubItem>
                ))}
              </FooterCategorySubItemList>
            </FooterCategoryItem>
          )}
          {/* <FooterCategoryItem>
            <FooterCategoryItemName>
              <FormattedMessage {...messages.customers} />
            </FooterCategoryItemName>
            <FooterCategorySubItemList>
              {customersData.map((customer) => (
                <FooterCategorySubItem key={customer} to="">
                  {messages[customer] ? <FormattedMessage {...messages[customer]} /> : customer}
                </FooterCategorySubItem>
              ))}
            </FooterCategorySubItemList>
          </FooterCategoryItem> */}
          {!!footer?.trendingSearch?.length && (
            <FooterCategoryItem>
              <FooterCategoryItemName>
                <FormattedMessage {...messages.trendingSearch} />
              </FooterCategoryItemName>
              <FooterCategorySubItemList>
                {footer.trendingSearch.map((keyword) => (
                  <FooterCategorySubItem key={keyword} to={`/market?q=${keyword}`}>
                    {keyword}
                  </FooterCategorySubItem>
                ))}
              </FooterCategorySubItemList>
            </FooterCategoryItem>
          )}
        </FooterSeoInner>
      </FooterSeoSection>

      {/* ── Dark Section ── */}
      <DarkSectionWrapper>
        <DarkSectionInner>
          <DarkMainContent>
            {/* Left column */}
            <LeftColumn>
              <KamereoContentWrapper>
                <LogoLink href="/" rel={!isHomePage ? 'nofollow' : undefined}>
                  <SVGIcon src={KamereoLogoWhite} renderAsImg={false} imgAttr={{ alt: 'Kamereo', loading: 'lazy' }} />
                </LogoLink>
                <Tagline>
                  <FormattedMessage {...messages.tagline} />
                </Tagline>
                <AppBadgesRow>
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href="https://apps.apple.com/vn/app/kamereo-nh%E1%BA%ADp-h%C3%A0ng-dn-f-b/id1469505654"
                  >
                    <SVGIcon src={lang === 'vi' ? AppStoreVi : AppStore} renderAsImg={false} />
                  </a>
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href="https://play.google.com/store/apps/details?id=vn.kamereo.buyer"
                  >
                    <SVGIcon src={lang === 'vi' ? GoogleStoreVi : GoogleStore} renderAsImg={false} />
                  </a>
                </AppBadgesRow>
                <CertBadgesRow>
                  <a
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    target="_blank"
                    href="http://online.gov.vn/Home/WebDetails/123467?AspxAutoDetectCookieSupport=1"
                  >
                    <SVGIcon
                      src={RegisteredIndustryAndTrade}
                      imgAttr={{ loading: 'lazy', width: '102px', alt: 'Ministry of Industry and Trade' }}
                    />
                  </a>
                  <a
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    ref={dmcaRef}
                    target="_blank"
                    href="//www.dmca.com/Protection/Status.aspx?ID=ac07edbf-327a-41ad-8737-603222e24d2d"
                    title="DMCA.com Protection Status"
                    className="dmca-badge"
                  >
                    <DMCA
                      src="https://images.dmca.com/Badges/dmca-badge-w150-2x1-04.png?ID=ac07edbf-327a-41ad-8737-603222e24d2d"
                      alt="DMCA.com Protection Status"
                      width="90px"
                      height="41px"
                      loading="lazy"
                    />
                  </a>
                </CertBadgesRow>
              </KamereoContentWrapper>
              <ContactList>
                <ContactItem>
                  <ContactIconWrapper>
                    <SVGIcon src={EmailIcon} renderAsImg={false} />
                  </ContactIconWrapper>
                  <ContactText>Email: info@kamereo.vn</ContactText>
                </ContactItem>
                <ContactItem>
                  <ContactIconWrapper>
                    <SVGIcon src={PhoneIcon} renderAsImg={false} />
                  </ContactIconWrapper>
                  <ContactText>
                    <FormattedMessage {...messages.phoneContact} />
                  </ContactText>
                </ContactItem>
                <ContactItem>
                  <ContactIconWrapper>
                    <SVGIcon src={HeadQuarter} renderAsImg={false} />
                  </ContactIconWrapper>
                  <ContactText>
                    <FormattedMessage {...messages.addressLabel} />
                  </ContactText>
                </ContactItem>
                <ContactTextAddress>
                  <FormattedMessage {...messages.theNorth} />: Detech Tower II, 107 Nguyễn Phong Sắc, Cầu Giấy, Hà Nội.
                  <div style={{ padding: '4px 0' }} />
                  <FormattedMessage {...messages.theSouth} />: Tòa nhà The Manor Officetel, 89 Nguyễn Hữu Cảnh, Thạnh Mỹ
                  Tây, TP Hồ Chí Minh.
                </ContactTextAddress>
              </ContactList>
            </LeftColumn>

            {/* Nav columns */}
            <NavColumnsWrapper>
              {/* Products */}
              <NavColumn>
                <NavColumnHeader>
                  <FormattedMessage {...messages.products} />
                </NavColumnHeader>
                <NavColumnLinks>
                  <NavLinkInternal to="/">
                    <FormattedMessage {...messages.shoppingNow} />
                  </NavLinkInternal>
                  <NavLinkInternal to={`${lang === 'vi' ? '/he-thong-phan-phoi' : '/he-thong-phan-phoi?lang=en'}`}>
                    <FormattedMessage {...messages.distributionLocation} />
                  </NavLinkInternal>
                  <NavLinkInternal to="/tu-nong-trai-den-doanh-nghiep">
                    <FormattedMessage {...messages.farmToBusiness} />
                  </NavLinkInternal>
                  <NavLinkInternal to="/kamereo-ngon">
                    <FormattedMessage {...messages.kamereoFresh} />
                  </NavLinkInternal>
                  <NavLinkInternal to="/kamereo-tien-dung">
                    <FormattedMessage {...messages.kamereoEssential} />
                  </NavLinkInternal>
                </NavColumnLinks>
              </NavColumn>

              {/* Resources */}
              <NavColumn>
                <NavColumnHeader>
                  <FormattedMessage {...messages.resources} />
                </NavColumnHeader>
                <NavColumnLinks>
                  <NavLinkExternal
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    href={`https://kamereo.vn/blog/${lang}/${lang === 'vi' ? 'kien-thuc-kinh-doanh/' : 'business-tips/'}`}
                    target="_blank"
                  >
                    <FormattedMessage {...messages.businessTips} />
                  </NavLinkExternal>
                  <NavLinkExternal
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    href={`https://kamereo.vn/blog/${lang}/${lang === 'vi' ? 'am-thuc-doi-song/' : 'food-and-lifestyle/'}`}
                    target="_blank"
                  >
                    <FormattedMessage {...mainNavMessages.foodAndLifeStyle} />
                  </NavLinkExternal>
                  <NavLinkExternal
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    href={`https://kamereo.vn/blog/${lang}/${
                      lang === 'vi' ? 'dia-diem-an-uong-vui-choi/' : 'place-to-eat-drink-entertain/'
                    }`}
                    target="_blank"
                  >
                    <FormattedMessage {...mainNavMessages.topPlaces} />
                  </NavLinkExternal>
                  <NavLinkExternal
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    href={`https://kamereo.vn/blog/${lang}/${lang === 'vi' ? 'chung-nhan/' : 'certifications/'}`}
                    target="_blank"
                  >
                    <FormattedMessage {...messages.certifications} />
                  </NavLinkExternal>
                </NavColumnLinks>
              </NavColumn>

              {/* Company */}
              <NavColumn>
                <NavColumnHeader>
                  <FormattedMessage {...messages.company} />
                </NavColumnHeader>
                <NavColumnLinks>
                  <NavLinkExternal
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    href={renderWithWebDomain(process.env.APP_ENV, '/ve-kamereo')}
                    target="_blank"
                  >
                    <FormattedMessage {...messages.aboutUs} />
                  </NavLinkExternal>
                  <NavLinkExternal rel="noopener noreferrer" href={`https://tuyendung.kamereo.vn`} target="_blank">
                    <FormattedMessage {...messages.careers} />
                  </NavLinkExternal>
                  <NavLinkExternal
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    href={`https://kamereo.vn/${lang === 'vi' ? 'gia-tri-cot-loi' : 'core-values'}`}
                    target="_blank"
                  >
                    <FormattedMessage {...messages.coreValues} />
                  </NavLinkExternal>
                  <NavLinkExternal
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    href={`https://kamereo.vn/blog/${lang}/${lang === 'vi' ? 'tin-tuc-kamereo/' : 'kamereo-news/'}`}
                    target="_blank"
                  >
                    <FormattedMessage {...messages.news} />
                  </NavLinkExternal>
                  <NavLinkExternal
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    href={`https://kamereo.vn/blog/${lang}/${
                      lang === 'vi' ? 'bao-chi-noi-gi-ve-kamereo/' : 'kamereo-in-the-newspaper/'
                    }`}
                    target="_blank"
                  >
                    <FormattedMessage {...messages.press} />
                  </NavLinkExternal>
                </NavColumnLinks>
              </NavColumn>

              {/* Support */}
              <NavColumn>
                <NavColumnHeader>
                  <FormattedMessage {...messages.supportSection} />
                </NavColumnHeader>
                <NavColumnLinks>
                  <NavLinkExternal
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    href={`${
                      lang === 'vi'
                        ? 'https://kamereo.vn/blog/wp-content/uploads/2026/04/PAYMENT_METHODS_POLICY_V1.1_VIE.pdf'
                        : 'https://kamereo.vn/blog/wp-content/uploads/2026/04/PAYMENT_METHODS_POLICY_V1.1_ENG.pdf'
                    }`}
                    target="_blank"
                  >
                    <FormattedMessage {...messages.payment} />
                  </NavLinkExternal>
                  <NavLinkExternal rel="noopener noreferrer" target="_blank" href={deliveryPolicy}>
                    <FormattedMessage {...messages.delivery} />
                  </NavLinkExternal>
                  <NavLinkExternal
                    rel={`noopener noreferrer${!isHomePage ? ' nofollow' : ''}`}
                    href={`${lang === 'vi' ? 'https://kamereo.vn/blog/wp-content/uploads/2026/04/RETURN_EXCHANGE_POLICY_1.1_vie.pdf' : 'https://kamereo.vn/blog/wp-content/uploads/2026/04/RETURN_EXCHANGE_POLICY_1.1_eng.pdf'}`}
                  >
                    <FormattedMessage {...messages.exchange} />
                  </NavLinkExternal>
                </NavColumnLinks>
              </NavColumn>
            </NavColumnsWrapper>
          </DarkMainContent>

          <DarkDivider />

          <BottomBar>
            <BottomLink
              target="_blank"
              href={
                lang === 'vi'
                  ? 'https://kamereo.vn/blog/wp-content/uploads/2024/11/Dieu-khoan-Dich-vu-Dieu-kien-Giao-dich-Chung.pdf'
                  : 'https://kamereo.vn/blog/wp-content/uploads/2024/11/TERMS-OF-SERVICE-GENERAL-TRANSACTION-CONDITIONS.pdf'
              }
              rel="nofollow"
            >
              <FormattedMessage {...contactUsPageMessages.terms} />
            </BottomLink>
            <BottomLink
              target="_blank"
              href={
                lang === 'vi'
                  ? 'https://kamereo.vn/blog/wp-content/uploads/2024/11/Chinh-Sach-Bao-Mat.pdf'
                  : 'https://kamereo.vn/blog/wp-content/uploads/2024/11/Privacy-Policy.pdf'
              }
              rel="nofollow"
            >
              <FormattedMessage {...contactUsPageMessages.privacyCondition} />
            </BottomLink>
            <CopyrightText>
              <FormattedMessage {...messages.allRightReserved} values={{ currentYear: new Date().getFullYear() }} />
            </CopyrightText>
            <SocialNetworkGroup />
          </BottomBar>
        </DarkSectionInner>
      </DarkSectionWrapper>
    </>
  );
};

const mapStateToProps = createStructuredSelector<
  RootState,
  Omit<IMainLayoutStateProps, 'isPenddingApproval' | 'countPendingOrder'>
>({
  mainLayout: makeSelectMainLayout(),
});

const withConnect = connect(mapStateToProps);

export default compose(injectIntl, withConnect, withRouter)(Footer);
